import dayjs from "dayjs";
import { UserProperties } from "@shared/types";
import { PRIMARY_EXCHANGES } from "@shared/common/constants";
import {
  CURRENCY_SYMBOL,
  DAILY_GRANULARITY_MAX_DAYS,
  FINANCE_DATE_FORMAT,
  GRANULARITY_TO_PERIOD_FORMAT,
  NEUTRAL_CATEGORIES,
  SUPERUSER_PERMISSION_LEVEL,
} from "features/finance/constants";
import {
  FinanceCategory,
  FinanceCurrency,
  FinanceDateRange,
  FinanceDirection,
  FinanceEntriesFilter,
  FinanceEntry,
  FinanceGranularity,
  FinanceSource,
  FinanceSummaryRow,
} from "features/finance/types";

export interface FinanceTotals {
  income: number;
  expenses: number;
  net: number;
  margin: number | null;
}

export interface FinancePeriod extends FinanceTotals {
  period: string;
}

type TotalsRow = Pick<FinanceSummaryRow, "direction" | "category" | "total">;

export interface SourceBreakdownRow {
  source: FinanceSource;
  total: number;
  share: number;
  exchanges?: ExchangeBreakdownRow[];
}

export interface ExchangeBreakdownRow {
  exchange: string;
  total: number;
  share: number;
}

export interface CategoryBreakdownRow {
  category: FinanceCategory;
  total: number;
  share: number;
  sources: SourceBreakdownRow[];
}

export const isSuperuser = (user?: Pick<UserProperties, "permission_level">) =>
  user?.permission_level === SUPERUSER_PERMISSION_LEVEL;

const isCounted = (row: Pick<TotalsRow, "category">) =>
  !NEUTRAL_CATEGORIES.includes(row.category);

const getTotals = (income: number, expenses: number): FinanceTotals => {
  const net = income - expenses;
  return {
    income,
    expenses,
    net,
    margin: income ? (net / income) * 100 : null,
  };
};

export const getFinanceTotals = (rows: TotalsRow[]): FinanceTotals => {
  let income = 0;
  let expenses = 0;
  for (const row of rows) {
    if (!isCounted(row)) continue;
    if (row.direction === FinanceDirection.INCOME) income += row.total;
    else expenses += row.total;
  }
  return getTotals(income, expenses);
};

export const getGranularity = (range: FinanceDateRange) =>
  dayjs(range.end_date).diff(range.start_date, "day") <
  DAILY_GRANULARITY_MAX_DAYS
    ? FinanceGranularity.DAY
    : FinanceGranularity.MONTH;

export const getPeriodsInRange = (
  range: FinanceDateRange,
  granularity: FinanceGranularity
) => {
  const unit = granularity === FinanceGranularity.DAY ? "day" : "month";
  const periods: string[] = [];
  let period = dayjs(range.start_date).startOf(unit);
  const last = dayjs(range.end_date).startOf(unit);
  while (!period.isAfter(last)) {
    periods.push(period.format(GRANULARITY_TO_PERIOD_FORMAT[granularity]));
    period = period.add(1, unit);
  }
  return periods;
};

export const getPeriodTotals = (
  rows: FinanceSummaryRow[],
  periods: string[]
): FinancePeriod[] =>
  periods.map((period) => ({
    period,
    ...getFinanceTotals(rows.filter((row) => row.period === period)),
  }));

const toRange = (start: dayjs.Dayjs, end: dayjs.Dayjs): FinanceDateRange => ({
  start_date: start.format(FINANCE_DATE_FORMAT),
  end_date: end.format(FINANCE_DATE_FORMAT),
});

export const getPreviousRange = (range: FinanceDateRange): FinanceDateRange => {
  const start = dayjs(range.start_date);
  const end = dayjs(range.end_date);
  if (start.date() !== 1) {
    const days = end.diff(start, "day") + 1;
    return toRange(start.subtract(days, "day"), end.subtract(days, "day"));
  }
  const months = end.diff(start, "month") + 1;
  const previousEnd = end.subtract(months, "month");
  const isMonthEnd = end.isSame(end.endOf("month"), "day");
  return toRange(
    start.subtract(months, "month"),
    isMonthEnd ? previousEnd.endOf("month") : previousEnd
  );
};

export const getMonthRange = (
  month: string,
  maxDate: dayjs.Dayjs
): [dayjs.Dayjs, dayjs.Dayjs] => {
  const start = dayjs(month).startOf("month");
  const end = start.endOf("month").startOf("day");
  return [start, end.isAfter(maxDate) ? maxDate : end];
};

export const getChange = (current: number, previous: number) =>
  previous ? ((current - previous) / Math.abs(previous)) * 100 : null;

export const hasEntriesFilter = (filter: FinanceEntriesFilter) =>
  !!(filter.direction || filter.source || filter.category || filter.exchange);

export const filterSummaryRows = (
  rows: FinanceSummaryRow[],
  filter: FinanceEntriesFilter
) =>
  rows.filter(
    (row) =>
      (!filter.direction || row.direction === filter.direction) &&
      (!filter.source || row.source === filter.source) &&
      (!filter.category || row.category === filter.category) &&
      (!filter.exchange || row.exchange === filter.exchange)
  );

const share = (value: number, total: number) =>
  total ? (value / total) * 100 : 0;

const sumBy = <K extends number | string>(
  rows: FinanceSummaryRow[],
  getKey: (row: FinanceSummaryRow) => K
) => {
  const totals = new Map<K, number>();
  for (const row of rows) {
    const key = getKey(row);
    totals.set(key, (totals.get(key) ?? 0) + row.total);
  }
  return [...totals.entries()].sort((a, b) => b[1] - a[1]);
};

export const getExpenseBreakdown = (
  rows: FinanceSummaryRow[]
): CategoryBreakdownRow[] => {
  const expenses = rows.filter(
    (row) => isCounted(row) && row.direction === FinanceDirection.EXPENSE
  );
  const total = expenses.reduce((sum, row) => sum + row.total, 0);
  return sumBy(expenses, (row) => row.category).map(
    ([category, categoryTotal]) => ({
      category,
      total: categoryTotal,
      share: share(categoryTotal, total),
      sources: sumBy(
        expenses.filter((row) => row.category === category),
        (row) => row.source
      ).map(([source, sourceTotal]) => ({
        source,
        total: sourceTotal,
        share: share(sourceTotal, total),
      })),
    })
  );
};

export const getIncomeBreakdown = (
  rows: FinanceSummaryRow[]
): SourceBreakdownRow[] => {
  const income = rows.filter(
    (row) => isCounted(row) && row.direction === FinanceDirection.INCOME
  );
  const total = income.reduce((sum, row) => sum + row.total, 0);
  return sumBy(income, (row) => row.source).map(([source, sourceTotal]) => ({
    source,
    total: sourceTotal,
    share: share(sourceTotal, total),
    ...(source === FinanceSource.EXCHANGE && {
      exchanges: sumBy(
        income.filter((row) => row.source === source && !!row.exchange),
        (row) => row.exchange!
      ).map(([exchange, exchangeTotal]) => ({
        exchange,
        total: exchangeTotal,
        share: share(exchangeTotal, total),
      })),
    }),
  }));
};

export const getFinanceExchangeLabel = (exchange: string) =>
  PRIMARY_EXCHANGES[exchange.toUpperCase()] ?? exchange;

export const getEntryAmount = (
  entry: FinanceEntry,
  currency: FinanceCurrency
) => (currency === FinanceCurrency.ILS ? entry.amount_ils : entry.amount_usd);

export const formatMoney = (
  value: number,
  currency: string,
  fractionDigits = 0
) => {
  const formatted = Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  const sign = value < 0 ? "−" : "";
  const symbol = CURRENCY_SYMBOL[currency];
  return symbol
    ? `${sign}${symbol}${formatted}`
    : `${sign}${formatted} ${currency}`;
};

export const formatCompactMoney = (value: number, currency: string) => {
  const abs = Math.abs(value);
  const sign = value < 0 ? "−" : "";
  const symbol = CURRENCY_SYMBOL[currency] ?? "";
  if (abs >= 1_000_000)
    return `${sign}${symbol}${+(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${symbol}${+(abs / 1_000).toFixed(1)}k`;
  return `${sign}${symbol}${Math.round(abs)}`;
};

export const formatPercent = (value: number | null) =>
  value === null ? "—" : `${value.toFixed(1)}%`;

export const formatChange = (value: number, unit = "%") =>
  `${value > 0 ? "+" : value < 0 ? "−" : ""}${Math.abs(value).toFixed(
    1
  )}${unit}`;

export const getEntryTotalsRows = (
  entries: FinanceEntry[],
  currency: FinanceCurrency
): TotalsRow[] =>
  entries.map((entry) => ({
    direction: entry.direction,
    category: entry.category,
    total: getEntryAmount(entry, currency),
  }));

const escapeCsvValue = (value: string | number) => {
  const text = String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildCsv = (rows: (string | number)[][]) =>
  rows.map((row) => row.map(escapeCsvValue).join(",")).join("\r\n");

export const downloadTextFile = (
  filename: string,
  content: string,
  type = "text/csv;charset=utf-8"
) => {
  const url = URL.createObjectURL(new Blob(["\ufeff", content], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const getRequestErrorMessage = (error: unknown): string | undefined => {
  const message = (error as { data?: { message?: unknown } } | undefined)?.data
    ?.message;
  return typeof message === "string" && message ? message : undefined;
};
